// src/pages/finance-portal/credit-memos/CreditMemoDetail.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Button, Card, CardContent, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Alert, CircularProgress, Divider, Tooltip, Autocomplete,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    FormControlLabel, Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT: { bg: '#fef3c7', color: '#92400e' },
    APPLIED: { bg: '#dcfce7', color: '#166534' },
    VOID: { bg: '#fecaca', color: '#991b1b' },
};

const REFUND_METHOD_LABELS = {
    ORIGINAL_PAYMENT: 'Original Payment',
    ACCOUNT_CREDIT: 'Account Credit',
    MANUAL_CHECK: 'Manual Check',
    NONE: 'None',
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function CreditMemoDetail() {
    const navigate = useNavigate();
    const { memoId } = useParams();

    const [memo, setMemo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Action states
    const [applying, setApplying] = useState(false);
    const [voiding, setVoiding] = useState(false);
    const [deletingMemo, setDeletingMemo] = useState(false);

    // Dialogs
    const [applyDialog, setApplyDialog] = useState(false);
    const [voidDialog, setVoidDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);

    // Apply dialog fields
    const [applyInvoice, setApplyInvoice] = useState(null);
    const [applyInvoiceOptions, setApplyInvoiceOptions] = useState([]);
    const [applyInvoiceSearch, setApplyInvoiceSearch] = useState('');
    const [applyInvoiceLoading, setApplyInvoiceLoading] = useState(false);
    const [createJournalEntry, setCreateJournalEntry] = useState(false);
    const applyInvoiceTimer = useRef(null);

    const fetchMemo = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/credit-memos/${memoId}`);
            if (res.data.success) {
                setMemo(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load credit memo');
        } finally {
            setLoading(false);
        }
    }, [memoId]);

    useEffect(() => { fetchMemo(); }, [fetchMemo]);

    // Fetch unpaid invoices for apply dialog
    useEffect(() => {
        if (applyInvoiceTimer.current) clearTimeout(applyInvoiceTimer.current);
        if (!applyDialog || !memo?.customer_id) {
            setApplyInvoiceOptions([]);
            return;
        }
        setApplyInvoiceLoading(true);
        applyInvoiceTimer.current = setTimeout(async () => {
            try {
                const params = {
                    customer_id: memo.customer_id,
                    status: 'unpaid',
                };
                if (applyInvoiceSearch) params.search = applyInvoiceSearch;
                const res = await axiosInstance.get('/api/finance/invoices', { params });
                if (res.data.success) {
                    setApplyInvoiceOptions(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to fetch invoices', err);
            } finally {
                setApplyInvoiceLoading(false);
            }
        }, 300);
        return () => { if (applyInvoiceTimer.current) clearTimeout(applyInvoiceTimer.current); };
    }, [applyDialog, memo?.customer_id, applyInvoiceSearch]);

    const openApplyDialog = () => {
        setApplyInvoice(null);
        setApplyInvoiceSearch('');
        setCreateJournalEntry(false);
        setApplyDialog(true);
    };

    const handleApply = async () => {
        setApplying(true);
        setError(null);
        try {
            const payload = {
                create_journal_entry: createJournalEntry,
            };
            if (applyInvoice) {
                payload.invoice_id = applyInvoice.id;
            }
            await axiosInstance.post(`/api/finance/credit-memos/${memoId}/apply`, payload);
            setApplyDialog(false);
            setSuccess('Credit memo applied successfully');
            fetchMemo();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to apply credit memo');
        } finally {
            setApplying(false);
        }
    };

    const handleVoid = async () => {
        setVoiding(true);
        setError(null);
        try {
            await axiosInstance.post(`/api/finance/credit-memos/${memoId}/void`);
            setVoidDialog(false);
            setSuccess('Credit memo voided successfully');
            fetchMemo();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to void credit memo');
        } finally {
            setVoiding(false);
        }
    };

    const handleDelete = async () => {
        setDeletingMemo(true);
        setError(null);
        try {
            await axiosInstance.delete(`/api/finance/credit-memos/${memoId}`);
            navigate('/finance-portal/credit-memos');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete credit memo');
        } finally {
            setDeletingMemo(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    if (!memo && !loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: 4 }}>
                <Alert severity="error" sx={{ borderRadius: '12px', fontFamily }}>
                    Credit memo not found.
                    <Button onClick={() => navigate('/finance-portal/credit-memos')} sx={{ fontFamily, textTransform: 'none', ml: 2 }}>
                        Back to Credit Memos
                    </Button>
                </Alert>
            </Box>
        );
    }

    const statusStyle = STATUS_COLORS[memo.status] || {};
    const customerName = memo.customer_name || memo.customer_company || memo.customer?.company_name || (memo.customer ? `${memo.customer.first_name || ''} ${memo.customer.last_name || ''}`.trim() : '\u2014');

    // Calculate totals from line items
    const lineItems = memo.line_items || [];
    const subtotal = lineItems.reduce((sum, li) => sum + ((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0)), 0);
    const totalTax = lineItems.reduce((sum, li) => sum + (parseFloat(li.tax_amount) || 0), 0);
    const total = memo.total != null ? memo.total : (subtotal + totalTax);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <IconButton onClick={() => navigate('/finance-portal/credit-memos')} sx={{ color: 'white', mr: 0.5 }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CreditScoreIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                    {memo.credit_memo_number || 'Credit Memo'}
                                </Typography>
                                <Chip
                                    label={getStatusLabel(memo.status)}
                                    size="small"
                                    sx={{
                                        fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 24,
                                        bgcolor: 'rgba(255,255,255,0.25)', color: 'white',
                                    }}
                                />
                            </Box>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Credit memo details</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {memo.status === 'DRAFT' && (
                            <Button
                                variant="contained"
                                startIcon={<CheckCircleIcon />}
                                onClick={openApplyDialog}
                                sx={{
                                    fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                    bgcolor: 'white', color: ACCENT,
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Apply
                            </Button>
                        )}
                        {(memo.status === 'DRAFT' || memo.status === 'APPLIED') && (
                            <Button
                                variant="contained"
                                startIcon={<BlockIcon />}
                                onClick={() => setVoidDialog(true)}
                                sx={{
                                    fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                    bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                }}
                            >
                                Void
                            </Button>
                        )}
                        {memo.status === 'DRAFT' && (
                            <Button
                                variant="contained"
                                startIcon={<DeleteIcon />}
                                onClick={() => setDeleteDialog(true)}
                                sx={{
                                    fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                    bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                }}
                            >
                                Delete
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Info Cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
                    {/* Customer Info */}
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <PersonIcon sx={{ fontSize: 20, color: ACCENT }} />
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase' }}>Customer</Typography>
                            </Box>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1rem', color: '#1e293b' }}>
                                {customerName}
                            </Typography>
                            {memo.customer?.email && (
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mt: 0.5 }}>
                                    {memo.customer.email}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>

                    {/* Credit Memo Info */}
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <CalendarTodayIcon sx={{ fontSize: 20, color: ACCENT }} />
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase' }}>Details</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Date</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{formatDate(memo.credit_date)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Refund Method</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{REFUND_METHOD_LABELS[memo.refund_method] || memo.refund_method || '\u2014'}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Status</Typography>
                                    <Chip
                                        label={getStatusLabel(memo.status)}
                                        size="small"
                                        sx={{
                                            fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                            bgcolor: statusStyle.bg, color: statusStyle.color,
                                        }}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Original Invoice */}
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <ReceiptLongIcon sx={{ fontSize: 20, color: ACCENT }} />
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase' }}>Original Invoice</Typography>
                            </Box>
                            {memo.original_invoice_id ? (
                                <Typography
                                    sx={{
                                        fontFamily, fontWeight: 600, fontSize: '1rem', color: ACCENT,
                                        cursor: 'pointer', '&:hover': { textDecoration: 'underline' },
                                    }}
                                    onClick={() => navigate(`/finance-portal/invoices/${memo.original_invoice_id}`)}
                                >
                                    {memo.original_invoice_number || memo.original_invoice?.invoice_number || `Invoice #${memo.original_invoice_id}`}
                                </Typography>
                            ) : (
                                <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#94a3b8' }}>No original invoice linked</Typography>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* Reason */}
                {memo.reason && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', mb: 1 }}>Reason</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', whiteSpace: 'pre-wrap' }}>{memo.reason}</Typography>
                        </CardContent>
                    </Card>
                )}

                {/* Applied Info */}
                {memo.status === 'APPLIED' && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #dcfce7', boxShadow: 'none', mb: 3, bgcolor: '#f0fdf4' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <CheckCircleIcon sx={{ fontSize: 20, color: '#166534' }} />
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#166534', textTransform: 'uppercase' }}>Applied</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {memo.applied_at && (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Applied at:</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{formatDate(memo.applied_at)}</Typography>
                                    </Box>
                                )}
                                {memo.applied_invoice_id && (
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Applied to Invoice:</Typography>
                                        <Typography
                                            sx={{
                                                fontFamily, fontSize: '0.85rem', fontWeight: 600, color: ACCENT,
                                                cursor: 'pointer', '&:hover': { textDecoration: 'underline' },
                                            }}
                                            onClick={() => navigate(`/finance-portal/invoices/${memo.applied_invoice_id}`)}
                                        >
                                            {memo.applied_invoice_number || `Invoice #${memo.applied_invoice_id}`}
                                        </Typography>
                                    </Box>
                                )}
                                {memo.journal_entry_id && (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Journal Entry:</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>JE-{memo.journal_entry_id}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Line Items */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Line Items</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>Description</TableCell>
                                        <TableCell align="center" sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', width: 80 }}>Qty</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', width: 120 }}>Unit Price</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', width: 120 }}>Tax</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', width: 120 }}>Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lineItems.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>No line items</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : lineItems.map((li, index) => {
                                        const lineAmt = (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0);
                                        return (
                                            <TableRow key={li.id || index} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b' }}>{li.description || '\u2014'}</TableCell>
                                                <TableCell align="center" sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{li.quantity}</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatCurrency(li.unit_price)}</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatCurrency(li.tax_amount)}</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(lineAmt)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Totals */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Box sx={{ minWidth: 280 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Subtotal</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(subtotal)}</Typography>
                                </Box>
                                {totalTax > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Tax</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(totalTax)}</Typography>
                                    </Box>
                                )}
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>Total</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 700, color: ACCENT }}>{formatCurrency(total)}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Notes */}
                {memo.notes && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', mb: 1 }}>Notes</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', whiteSpace: 'pre-wrap' }}>{memo.notes}</Typography>
                        </CardContent>
                    </Card>
                )}
            </Box>

            {/* Apply Dialog */}
            <Dialog open={applyDialog} onClose={() => setApplyDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Apply Credit Memo {memo?.credit_memo_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b', mb: 2 }}>
                        This will mark the credit memo as applied. You can optionally apply it to an unpaid invoice for the same customer.
                    </Typography>
                    <Autocomplete
                        size="small"
                        options={applyInvoiceOptions}
                        getOptionLabel={opt => opt ? `${opt.invoice_number || 'INV-???'} - ${formatCurrency(opt.total)}` : ''}
                        value={applyInvoice}
                        onChange={(_, val) => setApplyInvoice(val)}
                        onInputChange={(_, val) => setApplyInvoiceSearch(val)}
                        loading={applyInvoiceLoading}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Apply to Invoice (optional)"
                                placeholder="Search unpaid invoices..."
                                InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { fontFamily, borderRadius: '10px' },
                                    endAdornment: (
                                        <>
                                            {applyInvoiceLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        noOptionsText="No unpaid invoices found"
                        sx={{ mb: 2 }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={createJournalEntry}
                                onChange={e => setCreateJournalEntry(e.target.checked)}
                                sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                            />
                        }
                        label={<Typography sx={{ fontFamily, fontSize: '0.9rem' }}>Create Journal Entry</Typography>}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setApplyDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleApply}
                        disabled={applying}
                        startIcon={applying ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {applying ? 'Applying...' : 'Apply'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Void Dialog */}
            <Dialog open={voidDialog} onClose={() => setVoidDialog(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Void Credit Memo {memo?.credit_memo_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will void the credit memo. This action cannot be undone. The credit memo will no longer be applicable.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setVoidDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleVoid}
                        disabled={voiding}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
                    >
                        {voiding ? 'Voiding...' : 'Void Credit Memo'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Credit Memo {memo?.credit_memo_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will permanently delete the credit memo and all its line items. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deletingMemo}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deletingMemo ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
